import { ComponentInjector, ComponentSelector,ComponentSelectors, ComponentElements, ComponentObject, ComponentService, UIComponent, Inject , Injectable } from '@atypon/ui-utils';
import { keyCodes } from '../../../utility/js/utils';
import { VPortListener } from "ui-core/plugins/enquireJs/js/helper";
import {Bind,Bindable,isDescendant,isFocusable} from '../../../utility/ts';

@Injectable()
class CustomEvents {
    tooltipTabletOn = 'tooltip-tablet-on';
    tooltipTabletOff = 'tooltip-tablet-off';
}

class Texts extends ComponentObject {}

class ClassList extends ComponentObject {
    tooltipBody = 'tooltip__body';
    tooltipMetrics = 'tooltip__metrics';
    jsOpen = 'js--open';
    jsInverse = 'js--inverse';
}

class Selectors extends ComponentSelectors {
    tooltip = new ComponentSelector('.tooltip', 'document');
    tooltipBody = new ComponentSelector('.tooltip__body', '.tooltip');
    tooltipMetrics = new ComponentSelector('.tooltip__body', '.tooltip__metrics');
    tabContent = new ComponentSelector('.tab__content.sticko__child', 'document');
}

class Elements extends ComponentElements<Selectors> {
    tooltip: Array<HTMLElement> = []
    tooltipBody: Array<HTMLElement> = []
    tooltipMetrics: Array<HTMLElement> = []
    tabContent: Array<HTMLElement> = []
}

interface Tooltip extends ComponentService<Selectors, Elements, ClassList, Texts> {}

@Bindable
@ComponentInjector(Selectors, Elements, ClassList, Texts)
class Tooltip implements UIComponent {

    @Inject()
    customEvents:CustomEvents

    @VPortListener('md')
    isTablet: boolean;

    constructor(readonly wrapper: HTMLElement) {}

    initialize(): Tooltip {
        this.elements.initialize(this.wrapper);
        this.addEventListeners(); 
        return this;
    }

    isTabletOn (): void {
        this.isTablet = true;
        document.dispatchEvent(new CustomEvent(this.customEvents.tooltipTabletOn));
    }

    isTabletOff (): void {
        this.isTablet = false;
        document.dispatchEvent(new CustomEvent(this.customEvents.tooltipTabletOff));
    };

    addEventListeners(): void {
        this.domUtils.addEventListener(this.elements.tooltip, 'focus,click,keydown',this.handleTooltipEvents);
        this.domUtils.addEventListener(window as any, 'resize', () => this.setPosition(this.elements.tooltip));
        this.domUtils.addEventListener(window as any, 'scroll', () => this.hideTooltip());
        this.domUtils.addEventListener(this.elements.tabContent, 'scroll', () => this.hideTooltip());
        this.domUtils.addEventListener(document, 'click',this.handleDocumentClick);
        this.domUtils.addEventListener(this.elements.tooltipMetrics, 'keydown',this.handleMetricsEvents);
        this.domUtils.addEventListener(document , `${this.customEvents.tooltipTabletOn},${this.customEvents.tooltipTabletOff}`,this.handleViewportChange);
    }

    removeEventListeners(): void {
        this.domUtils.removeEventListener(this.elements.tooltip, 'focus,click,keydown',this.handleTooltipEvents);
        this.domUtils.removeEventListener(window as any, 'resize', () => this.setPosition(this.elements.tooltip));
        this.domUtils.removeEventListener(window as any, 'scroll', () => this.hideTooltip());
        this.domUtils.removeEventListener(this.elements.tabContent, 'scroll', () => this.hideTooltip());
        this.domUtils.removeEventListener(document, 'click',this.handleDocumentClick);
        this.domUtils.removeEventListener(this.elements.tooltipMetrics, 'keydown',this.handleMetricsEvents);
        this.domUtils.removeEventListener(document , `${this.customEvents.tooltipTabletOn},${this.customEvents.tooltipTabletOff}`,this.handleViewportChange);
    }

    reinitialize(): void {
        this.removeEventListeners();
        this.initialize();
    }

    @Bind
    handleViewportChange(): void {
        this.hideTooltip();
        this.reinitialize();
    }

    @Bind
    handleTooltipEvents(e: Event): void {
        const element = e.currentTarget as HTMLElement;
        const tooltipBody = this.domUtils.getElement(this.selectors.tooltipBody.selector, element);

        if (isFocusable(element)) {
            switch (e.type) {
                case 'focus':
                    this.showTooltip(element);
                    break;
                case 'click':
                    this.showTooltip(element);
                    break;
                case 'keydown':
                    const keyboardEvent = e as KeyboardEvent;
                    if (keyboardEvent.keyCode === keyCodes.ESCAPE) {
                        this.hideTooltip(element);
                    } else if (keyboardEvent.keyCode === keyCodes.TAB || keyboardEvent.keyCode === keyCodes.TAB && keyboardEvent.shiftKey) {
                        setTimeout(() => {                            
                            if(!isDescendant(document.activeElement as HTMLElement, [tooltipBody])){
                                this.hideTooltip(element);
                            } 
                        }, 0);
                    }
                    break;
            }
        } else {
            if (tooltipBody?.classList.contains(this.classList.jsOpen) && !this.isTablet) {
                this.hideTooltip(element);
            } else {
                this.showTooltip(element);
            }
        }
    }

    @Bind
    handleMetricsEvents(e: Event): void {
        const keyboardEvent = e as KeyboardEvent;
        const anchorElement = keyboardEvent.target as HTMLAnchorElement
        if (anchorElement.getAttribute("href") && (keyboardEvent.keyCode === keyCodes.RETURN || keyboardEvent.keyCode === keyCodes.SPACE)) {
            this.setHref(anchorElement?.href);
        }
    }

    setHref(href:string): void {
        window.location.href = href;
    }

    @Bind
    handleDocumentClick(e: Event): void {
        const target = e.target as HTMLElement;
        if (!isDescendant(target, this.elements.tooltip) && this.elements.tooltipBody.some(body => body.classList.contains(this.classList.jsOpen))) {
            this.hideTooltip();
        }
    }

    @Bind
    setPosition(elements: HTMLElement[]): void {
        elements.forEach(element => {
            const tooltipBody = this.domUtils.getElement(this.selectors.tooltipBody.selector, element);
            if (tooltipBody) {
                const elementRect = element.getBoundingClientRect();
                const windowWidth = window.innerWidth;
                const availableWidth = windowWidth - (elementRect.right + 20);
                const mobileMargin = 20;
                const inverseMargin = 30;

                if (this.isTablet) {
                    const wSlideTop = this.domUtils.closest('.w-slide' , element)?.getBoundingClientRect().top || 0;
                    const mobileTop = elementRect.top + 20 - wSlideTop;


                    tooltipBody.style.cssText = `
                        top: ${mobileTop}px;
                        right: ${mobileMargin}px;
                        left: ${mobileMargin}px;
                        width: auto;
                    `;
                } else {
                    if (availableWidth < tooltipBody.offsetWidth) {
                        tooltipBody.classList.add(this.classList.jsInverse);
                        tooltipBody.style.cssText = `
                            top: ${elementRect.top - tooltipBody.offsetHeight / 2 + element.offsetHeight / 2}px;
                            left: ${elementRect.left - inverseMargin - tooltipBody.offsetWidth}px;
                        `;
                    } else {
                        tooltipBody.classList.remove(this.classList.jsInverse);
                        tooltipBody.style.cssText = `
                            top: ${elementRect.top - tooltipBody.offsetHeight / 2 + element.offsetHeight / 2}px;
                            left: ${elementRect.right + mobileMargin}px;
                        `;
                    }
                }
            }
        });
    }

    showTooltip(tooltipTrigger: HTMLElement): void {
        const tooltipBody = this.domUtils.getElement(this.selectors.tooltipBody.selector, tooltipTrigger);
        tooltipBody.classList.add(this.classList.jsOpen);
        tooltipTrigger.setAttribute('aria-expanded', 'true');
        this.domUtils.getElements(this.selectors.tooltipBody.selector).forEach(body => {
            if (body !== tooltipBody) {
                body.classList.remove(this.classList.jsOpen);
            }
        });
        this.setPosition([tooltipTrigger]);
    }

    @Bind
    hideTooltip(tooltips?: HTMLElement | HTMLElement[]): void {
        if (!tooltips) {
            tooltips = this.elements.tooltip;
        }
        const tooltipsArray = Array.isArray(tooltips) ? tooltips : [tooltips];
        this.hideAll(tooltipsArray);
    }
    
    hideAll(tooltips: HTMLElement[]): void {
        tooltips.forEach((tooltipTrigger) => {
            const tooltipBody = this.domUtils.getElement(this.selectors.tooltipBody.selector, tooltipTrigger);
            tooltipBody?.classList.remove(this.classList.jsOpen, this.classList.jsInverse);
            tooltipBody?.removeAttribute('style');
            tooltipTrigger.setAttribute('aria-expanded', 'false');
        });
    }
}

export default Tooltip;